import React from 'react';
import { connect } from 'react-redux';
import { KeyLicenseTypes } from '../common/keyLicenseTypes';
import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import OfflineLicenseTable from '../components/offlineLicenseTable';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';
import * as offlineLicensesActions from '../store/offlineLicensesSlice';
import { offlineLicensesSelectors } from '../store/offlineLicensesSlice';
import OfflineLicenseHistoryDialog from './offlineLicenseHistoryDialog';
import RemoveRestoreLicensesConfirmDialog from './removeRestoreLicsConfirmDialog';
import OfflineLicenseEditDialog from './offlineLicenseEditDialog';
import setTitleToFilters from '../common/filtersTitles';
import { isClipboardAvailable } from '../common/clipboardUtils';
import { copyLicenseKeysToMemory } from '../common/licenseUtils';
import { showRowIdByCtrlClick } from '../common/tableActions';

import './common.css';
import { userConfigsSelectors } from '../store/userConfigs';
import { unwrapResult } from '@reduxjs/toolkit';
import ModalMessage from './modalMessage';
import { showModalMessage } from '../store/modalsSlice';

const thatObjectName = 'offlineLicenses';

class OfflineLicensesContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showRemoveRestoreLicensesConfirmDialog: false,
      removeRestoreLicensesConfirmDialogMode: null,
      showHistoryDialog: false,
      showOfflineEditDialog: false,
      showExtraInfoDialog: false,
    };
  }

  componentDidMount() {
    setTitleToFilters();
    this.props.fetchProducts();
    this.props.fetchOfflineLicenses();
    this.props.fetchLicLivingStates();
    this.props.dicomConnectionLimitationTypes();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>Offline licenses</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderOfflineLicenseEditDialog()}
          {this.renderRemoveRestoreLicensesConfirmDialog()}
          {this.renderHistoryDialog()}
          {this.renderModalMessage()}
        </div>
      </OverlayLoader>
    );
  }

  renderModalMessage = () => <ModalMessage />;

  renderButtons = () => (
    <TooltipButtonGroup>
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.props.fetchOfflineLicenses} />
      <TooltipElement type="button" text="Edit license" icon="edit" onClick={this.showEditOfflineLicenseDialog}
        disabled={this.isEditButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Remove licenses" icon="remove" onClick={this.removeOfflineLicenses}
        disabled={this.isRemoveButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Restore licenses" icon="repeat" onClick={this.restoreOfflineLicenses}
        disabled={this.isRestoreButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Show license history" icon="list-alt" onClick={this.showLicenseHistory}
        disabled={this.isHistoryButtonDisabled()} />
      <TooltipElement type="button" text="Download license" icon="download" onClick={this.downloadOfflineLicense}
        disabled={this.isDownloadButtonDisabled()} displayOnlyForWriter />
      {!isClipboardAvailable() ? <React.Fragment /> :
        <TooltipElement type="button" text="Copy to memory" icon="copy" onClick={copyLicenseKeysToMemory(this.props.selectedLicenses)}
          disabled={this.isCopyToMemoryButtonDisabled()} displayOnlyForWriter />
      }

    </TooltipButtonGroup>
  );

  isEditButtonDisabled = () => {
    if (this.props.selectedLicenses.length !== 1) return true;
    let lic = this.props.selectedLicenses[0];

    return !lic || lic.type === null || lic.type.deprecated || !lic.isAlive;
  };

  getActionsForFavorites = () => ({
    addToFavorites: this.props.addOfflineLicenseToFavorites,
    removeFromFavorites: this.props.removeOfflineLicenseFromFavorites,
  });

  renderContent = () => {
    if (this.props.offlineLicensesFetchingError !== null)
      return (
        <p className="text-danger">Error: {this.props.offlineLicensesFetchingError.toString()}</p>
      );
    const tableRowEvents = {
      onClick: this.handleRowClick,
    };

    return (
      <TableSelectionManager
        items={this.props.offlineLicenses}
        objectName={thatObjectName}
      >
        <OfflineLicenseTable
          offlineLicenses={this.props.offlineLicenses}
          products={this.props.productsState.data}
          livingStates={this.props.livingStatesState.data}
          dicomConnectionLimitationTypes={this.props.dicomConnectionLimitationTypesState.data}
          actions={this.getActionsForFavorites()}
          rowEvents={tableRowEvents}
        />
      </TableSelectionManager>
    );
  };

  handleRowClick = (e, row) => {
    e.stopPropagation();
    showRowIdByCtrlClick(e, row, 'License ID');
  }

  renderOfflineLicenseEditDialog = () => (
    <OfflineLicenseEditDialog
      mode={OfflineLicenseEditDialog.Mode.Edit}
      onClose={this.closeOfflineLicenseDialog}
      show={this.state.showOfflineEditDialog}
      licenseId={this.getSelectedLicenseFieldValue('id')}
      licenseKey={this.getSelectedLicenseFieldValue('key')}
      deviceKey={this.getSelectedLicenseFieldValue('device.key')}
      supportDayCount={this.getSelectedLicenseFieldValue('supportDayCount')}
      activationDate={this.getSelectedLicenseFieldValue('activationDate')}
      webConnectionCount={this.getSelectedLicenseFieldValue('webConnectionCount')}
      connectionCount={this.getSelectedLicenseFieldValue('connectionCount', 0)}
      webValidityTerm={this.getSelectedLicenseFieldValue('webValidityTerm')}
      dicomConnectionLimitationType={this.getSelectedLicenseFieldValue('dicomConnectionLimitationType')}
    />
  );

  getSelectedLicenseFieldValue = (fieldName, defaultValue = null) => {
    const license = this.props.selectedLicenses[0];
    if (!license) return defaultValue;
    try {
      const value = fieldName.split('.').reduce((acc, key) => acc[key], license);
      return value === undefined ? defaultValue : value === null ? defaultValue : value;
    } catch (e) {
      return defaultValue;
    }
  };

  showEditOfflineLicenseDialog = () => {
    this.setState({ showOfflineEditDialog: true });
  };

  closeOfflineLicenseDialog = () => {
    this.setState({ showOfflineEditDialog: false });
  };

  isLoading = () => this.props.isOfflineLicensesFetching ||
    this.props.productsState.isFetching ||
    this.props.isOfflineLicensesRemoving ||
    this.props.isOfflineLicensesRestoring ||
    this.props.dicomConnectionLimitationTypesState.isFetching ||
    this.props.isOfflineLicUpdating;

  getLoaderMessage = () => {
    if (this.props.isOfflineLicensesFetching) {
      return "Offline licenses loading...";
    }
    if (this.props.isOfflineLicUpdating) {
      return "Offline licenses updating...";
    }
    if (this.props.productsState.isFetching) {
      return "Product names loading...";
    }
    if (this.props.isOfflineLicensesRemoving) {
      return "Offline licenses removing...";
    }
    if (this.props.isOfflineLicensesRestoring) {
      return "Offline licenses restoring...";
    }
  };

  isRemoveButtonDisabled = () => {
    const lics = this.props.selectedLicenses;
    return lics.length === 0 || lics.find(lic => !lic.isAlive) !== undefined;
  };

  isRestoreButtonDisabled = () => {
    const lics = this.props.selectedLicenses;
    return lics.length === 0 ||
      lics.find(lic => lic.isAlive) !== undefined;
  };

  renderRemoveRestoreLicensesConfirmDialog = () => (
    <RemoveRestoreLicensesConfirmDialog
      show={this.state.showRemoveRestoreLicensesConfirmDialog}
      onClose={this.closeRemoveRestoreLicensesConfirmDialog}
      licenseIds={this.props.selectedLicensesIds}
      mode={this.state.removeRestoreLicensesConfirmDialogMode}
      licenseType={KeyLicenseTypes.Offline}
    />
  );

  removeOfflineLicenses = () => {
    this.setState({
      removeRestoreLicensesConfirmDialogMode: RemoveRestoreLicensesConfirmDialog.Mode.Remove,
      showRemoveRestoreLicensesConfirmDialog: true,
    });
  };

  restoreOfflineLicenses = () => {
    this.setState({
      removeRestoreLicensesConfirmDialogMode: RemoveRestoreLicensesConfirmDialog.Mode.Restore,
      showRemoveRestoreLicensesConfirmDialog: true,
    });
  };

  closeRemoveRestoreLicensesConfirmDialog = () => {
    this.setState({ showRemoveRestoreLicensesConfirmDialog: false });
  };

  isHistoryButtonDisabled = () => this.props.selectedLicenses.length !== 1;

  renderHistoryDialog = () => {
    return (
      <OfflineLicenseHistoryDialog
        show={this.state.showHistoryDialog}
        onClose={this.closeHistoryDialog}
        id={this.getSelectedLicenseFieldValue('id')}
        licenseKey={this.getSelectedLicenseFieldValue('key')}
      />
    );
  };

  showLicenseHistory = () => {
    this.setState({ showHistoryDialog: true });
  };

  closeHistoryDialog = () => {
    this.setState({ showHistoryDialog: false });
  };

  showLicenseExtraInfo = () => {
    this.setState({ showExtraInfoDialog: true });
  };

  closeLicenseExtraInfoDialog = () => {
    this.setState({ showExtraInfoDialog: false });
  };

  isDownloadButtonDisabled = () => {
    return this.props.selectedLicenses.length !== 1 ||
      this.getSelectedLicenseFieldValue('productName') !== 'pacs_server' ||
      this.getSelectedLicenseFieldValue('type') === null ||
      this.getSelectedLicenseFieldValue('type.code') !== '00';
  };

  downloadOfflineLicense = () => {
    const licenseId = this.getSelectedLicenseFieldValue('id');
    if (licenseId === null)
      return;
    this.props.downloadLicense({ licenseId })
      .then(unwrapResult)
      .catch(() => {
        this.props.showModalMessage("Error", this.props.downloadingError.toString());
      });
  };

  isCopyToMemoryButtonDisabled = () => this.props.selectedLicenses.length === 0;
}

const mapStateToProps = state => ({
  isOfflineLicensesFetching: offlineLicensesSelectors.isFetching(state),
  isOfflineLicensesRemoving: offlineLicensesSelectors.isRemoving(state),
  isOfflineLicensesRestoring: offlineLicensesSelectors.isRestoring(state),
  isOfflineLicUpdating: offlineLicensesSelectors.isUpdating(state),

  offlineLicenses: offlineLicensesSelectors.selectAll(state),

  productsState: objectDataSelectors.offlineLicenseProductsState(state),

  livingStatesState: objectDataSelectors.offlineLicenseLivingStatesState(state),
  dicomConnectionLimitationTypesState: objectDataSelectors.dicomConnectionLimitationTypesState(state),


  offlineLicensesFetchingError: offlineLicensesSelectors.fetchingError(state),
  downloadingError: offlineLicensesSelectors.downloadingError(state),

  selectedLicensesIds: userConfigsSelectors.selectedItems(state, thatObjectName),
  selectedLicenses: offlineLicensesSelectors.selectedLicenses(state)
});

const actionCreators = {
  fetchOfflineLicenses: offlineLicensesActions.fetchAll,
  fetchProducts: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.offlineLicenseProducts }),
  fetchLicLivingStates: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.offlineLicenseLivingStates }),
  dicomConnectionLimitationTypes: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.dicomConnectionLimitationTypes }),
  downloadLicense: offlineLicensesActions.download,

  addOfflineLicenseToFavorites: offlineLicensesActions.addToFavorites,
  removeOfflineLicenseFromFavorites: offlineLicensesActions.removeFromFavorites,
  showModalMessage: (title, text) => showModalMessage({ title, text }),
};

export default connect(mapStateToProps, actionCreators)(OfflineLicensesContainer);



// WEBPACK FOOTER //
// ./src/containers/offlineLicensesContainer.js